import React, { useCallback } from 'react';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import FormField from './FormField';

export interface NumericStepperProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  /** Allow decimal input (e.g. currency). Default: false (integers only) */
  decimal?: boolean;
  /** Step amount for up/down buttons. Default: 1 (or 0.01 when decimal=true) */
  step?: number;
  /** Minimum allowed value. Default: 0 for integers, no limit for decimals */
  min?: number;
  className?: string;
}

const NumericStepper = React.forwardRef<HTMLInputElement, NumericStepperProps>(
  (
    {
      label,
      value,
      onChange,
      placeholder,
      error,
      hint,
      required = false,
      disabled = false,
      decimal = false,
      step,
      min,
      className = '',
    },
    ref,
  ) => {
    const resolvedStep = step ?? (decimal ? 0.01 : 1);

    const sanitize = useCallback(
      (raw: string) => {
        if (decimal) {
          // Allow digits, one dot, optional leading minus; strips thousand separators on paste
          return raw.replace(/[^\d.]/g, '').replace(/^(\d*\.?\d*).*$/, '$1');
        }
        return raw.replace(/\D/g, '');
      },
      [decimal],
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(sanitize(e.target.value));
    };

    const increment = () => {
      const n = parseFloat(value) || 0;
      const next = parseFloat((n + resolvedStep).toFixed(decimal ? 2 : 0));
      onChange(String(next));
    };

    const decrement = () => {
      const n = parseFloat(value) || 0;
      const next = parseFloat((n - resolvedStep).toFixed(decimal ? 2 : 0));
      const bounded = min !== undefined ? Math.max(min, next) : next;
      onChange(String(bounded));
    };

    const borderColor = error
      ? 'border-red-400'
      : 'border-subtle hover:border-default focus-within:border-brand/60';

    return (
      <FormField label={label} error={error} hint={hint} required={required}>
        <div
          className={`flex items-stretch rounded-xl border transition-colors ${borderColor} bg-surface-primary ${disabled ? 'opacity-50 pointer-events-none' : ''} ${className}`}
        >
          <input
            ref={ref}
            inputMode={decimal ? 'decimal' : 'numeric'}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            className="flex-1 min-w-0 bg-transparent text-sm text-content px-3 py-2.5 outline-none placeholder:text-content-faint"
          />
          <div className="flex flex-col border-l border-subtle shrink-0">
            <button
              type="button"
              tabIndex={-1}
              disabled={disabled}
              onClick={increment}
              className="flex flex-1 items-center justify-center w-8 text-content-faint hover:text-content hover:bg-surface-secondary/70 rounded-tr-xl transition-colors border-b border-subtle"
              aria-label="Increment"
            >
              <ChevronUp size={13} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              tabIndex={-1}
              disabled={disabled}
              onClick={decrement}
              className="flex flex-1 items-center justify-center w-8 text-content-faint hover:text-content hover:bg-surface-secondary/70 rounded-br-xl transition-colors"
              aria-label="Decrement"
            >
              <ChevronDown size={13} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </FormField>
    );
  },
);

NumericStepper.displayName = 'NumericStepper';

export default NumericStepper;
